/**
 * IT training (16 Oct): the pictures of the "Priority, SLA and escalation"
 * lesson — the coloured priority matrix, the commitments table and the
 * escalation timeline. Every number comes from `lib/sla-reference.ts`; the
 * words are UI labels, so they follow the reader's language.
 */

import { Badge, Card, Typography } from "@norican/react";

import { useT } from "@/i18n";

import type { LessonWidget } from "./lib/lessons";
import {
  SLA_ESCALATION,
  SLA_LEVELS,
  SLA_MATRIX,
  SLA_PRIORITIES,
  SLA_TARGETS,
  type SlaLevel,
  type SlaRecipient,
} from "./lib/sla-reference";
import { PriorityChip, useDuration } from "./sla-parts";

function SlaMatrix() {
  const { t } = useT("tickets");
  const level = (name: SlaLevel) => t(`itAdmin.training.sla.level.${name}`);
  return (
    <figure className="flex flex-col gap-2">
      <table className="w-full max-w-md table-fixed border-separate border-spacing-1">
        <thead>
          <tr>
            <th scope="col" className="text-fg-muted text-left align-bottom text-xs font-medium">
              {t("itAdmin.training.sla.impact")} ↓ / {t("itAdmin.training.sla.urgency")} →
            </th>
            {SLA_LEVELS.map((urgency) => (
              <th
                key={urgency}
                scope="col"
                className="bg-bg-inverse text-fg-on-inverse rounded-md py-2 text-sm"
              >
                {level(urgency)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {SLA_LEVELS.map((impact) => (
            <tr key={impact}>
              <th scope="row" className="bg-bg-inverse text-fg-on-inverse rounded-md px-2 text-sm">
                {level(impact)}
              </th>
              {SLA_LEVELS.map((urgency) => (
                <td key={urgency} className="p-0">
                  <PriorityChip priority={SLA_MATRIX[impact][urgency]} large />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <Typography as="figcaption" variant="caption" tone="muted">
        {t("itAdmin.training.sla.matrixCaption")}
      </Typography>
    </figure>
  );
}

function SlaTargets() {
  const { t } = useT("tickets");
  const duration = useDuration();
  return (
    <figure className="flex flex-col gap-2">
      <table className="w-full max-w-md border-collapse text-sm">
        <thead>
          <tr className="border-border border-b">
            <th scope="col" className="py-2 text-left">
              {t("itAdmin.training.sla.priority")}
            </th>
            <th scope="col" className="py-2 text-left">
              {t("itAdmin.training.sla.response")}
            </th>
            <th scope="col" className="py-2 text-left">
              {t("itAdmin.training.sla.resolution")}
            </th>
          </tr>
        </thead>
        <tbody>
          {SLA_PRIORITIES.map((priority) => (
            <tr key={priority} className="border-border-subtle border-b">
              <th scope="row" className="py-1.5 text-left">
                <PriorityChip priority={priority} />
              </th>
              <td className="py-1.5">{duration(SLA_TARGETS[priority].response)}</td>
              <td className="py-1.5">{duration(SLA_TARGETS[priority].resolution)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Typography as="figcaption" variant="caption" tone="muted">
        {t("itAdmin.training.sla.targetsCaption")}
      </Typography>
    </figure>
  );
}

function Recipients({ who }: { who: readonly SlaRecipient[] }) {
  const { t } = useT("tickets");
  if (who.length === 0) {
    return (
      <Typography as="span" variant="body-sm" tone="muted">
        {t("itAdmin.training.sla.noNotification")}
      </Typography>
    );
  }
  return (
    <Typography as="span" variant="body-sm" className="font-semibold">
      {who.map((name) => t(`itAdmin.training.sla.recipient.${name}`)).join(", ")}
    </Typography>
  );
}

function SlaEscalation() {
  const { t } = useT("tickets");
  return (
    <figure className="flex flex-col gap-2">
      <ol className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {SLA_ESCALATION.map((step) => (
          <li key={step.at}>
            <Card
              variant="outline"
              padding="sm"
              accent="left"
              accentTone={step.at >= 100 ? "danger" : step.at >= 75 ? "warning" : "accent"}
              className="flex h-full flex-col gap-1"
            >
              <span className="flex items-center gap-2">
                <Typography as="span" variant="heading-6">
                  {step.at}%
                </Typography>
                {step.reminder === true ? (
                  <Badge tone="neutral" variant="subtle" size="sm">
                    {t("itAdmin.training.sla.reminder")}
                  </Badge>
                ) : null}
                {step.at >= 100 ? (
                  <Badge tone="danger" variant="subtle" size="sm">
                    {t("itAdmin.training.sla.breached")}
                  </Badge>
                ) : null}
              </span>
              <span className="flex flex-wrap gap-x-1">
                <Typography as="span" variant="body-sm" tone="muted">
                  {t("itAdmin.training.sla.response")}:
                </Typography>
                <Recipients who={step.response} />
              </span>
              <span className="flex flex-wrap gap-x-1">
                <Typography as="span" variant="body-sm" tone="muted">
                  {t("itAdmin.training.sla.resolution")}:
                </Typography>
                <Recipients who={step.resolution} />
              </span>
            </Card>
          </li>
        ))}
      </ol>
      <Typography as="figcaption" variant="caption" tone="muted">
        {t("itAdmin.training.sla.escalationCaption")}
      </Typography>
    </figure>
  );
}

export function LessonWidgetBlock({ name }: { name: LessonWidget }) {
  switch (name) {
    case "sla-matrix":
      return <SlaMatrix />;
    case "sla-targets":
      return <SlaTargets />;
    case "sla-escalation":
      return <SlaEscalation />;
  }
}
